import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Plus, Star } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import SignatureBuilderDialog, { madeInBuilder } from '../signatures/SignatureBuilderDialog';
import useShareTargets from '../../hooks/useShareTargets';
import { errorText } from '../../lib/apiErrors';
import { hasSenderFields, toSenderFields } from '../../lib/signatureMergeFields';
import { resolveSigVars } from '../../lib/signatureMergeVars';
import { ShareTargets, VisibilityCards } from './SharingPicker';
import { ItemGroup, ItemRow } from './SharedItemList';
import { splitByOwner } from '../../lib/sharedItems';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const EMPTY_FORM = {
    name: '', content: '', is_default: false,
    visibility: 'private', shared_with: [], shared_with_groups: [],
};

/**
 * Signatures tab of Settings → Templates & Signatures (#955).
 *
 * New signatures are built from `{{Sender*}}` merge fields (SignatureEditor's
 * `mergeFields` mode), which composers resolve against whoever sends. When an
 * owner shares a signature that has none — an older one with their details
 * typed in — the form warns that recipients would send the owner's name, and
 * for a structured signature offers to switch its details to merge fields.
 *
 * The org-wide override and default-signature behaviour are untouched: the
 * server never marks someone else's signature as the caller's default.
 */
export default function MySignaturesTab() {
    const { user, getAuthHeader } = useAuth();
    const queryClient = useQueryClient();
    const [signatures, setSignatures] = useState([]);
    const [adminSignatures, setAdminSignatures] = useState([]);
    const [overrideId, setOverrideId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);   // null = closed, {} = new, signature = edit
    const [form, setForm] = useState(EMPTY_FORM);
    const [signatureData, setSignatureData] = useState(null);
    const [editorKey, setEditorKey] = useState(0);
    const [htmlOnly, setHtmlOnly] = useState(false);
    const [saving, setSaving] = useState(false);
    const [copying, setCopying] = useState(null);
    const { members, groups } = useShareTargets(editing !== null);

    const load = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/signatures`, { headers: getAuthHeader() });
            setSignatures(res.data?.signatures || []);
            setAdminSignatures(res.data?.admin_templates || []);
            setOverrideId(res.data?.active_override_id || null);
        } catch (err) {
            toast.error(errorText(err, 'Failed to load signatures'));
        } finally {
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { load(); }, [load]);

    const refresh = async () => {
        await load();
        // Composers cache the signature list; refresh it so changes show there too.
        queryClient.invalidateQueries({ queryKey: ['signatures'] });
    };

    const openNew = () => {
        setForm(EMPTY_FORM);
        setSignatureData(null);
        setHtmlOnly(false);
        setEditorKey(k => k + 1);
        setEditing({});
    };

    const openEdit = (s) => {
        setForm({
            name: s.name || '',
            content: s.content || '',
            is_default: !!s.is_default,
            visibility: s.visibility === 'shared' ? 'shared' : 'private',
            shared_with: s.shared_with || [],
            shared_with_groups: s.shared_with_groups || [],
        });
        setSignatureData(s.signature_data || null);
        setHtmlOnly(!madeInBuilder(s.signature_data));
        setEditorKey(k => k + 1);
        setEditing(s);
    };

    const convertToMergeFields = () => {
        const converted = toSenderFields(signatureData);
        if (!converted) return;
        setSignatureData(converted);
        setEditorKey(k => k + 1);   // SignatureEditor reads signatureData on mount
    };

    const handleSave = async () => {
        if (!form.name.trim() || !form.content.trim()) {
            toast.error('Name and signature are required');
            return;
        }
        const shared = form.visibility === 'shared';
        const payload = {
            name: form.name.trim(),
            content: form.content,
            signature_data: signatureData,
            is_default: form.is_default,
            visibility: form.visibility,
            shared_with: shared ? form.shared_with : [],
            shared_with_groups: shared ? form.shared_with_groups : [],
        };
        setSaving(true);
        try {
            if (editing?.signature_id) {
                await axios.put(`${API}/signatures/${editing.signature_id}`, payload, { headers: getAuthHeader() });
                toast.success('Signature updated');
            } else {
                await axios.post(`${API}/signatures`, payload, { headers: getAuthHeader() });
                toast.success('Signature created');
            }
            setEditing(null);
            await refresh();
        } catch (err) {
            toast.error(errorText(err, 'Failed to save signature'));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (s) => {
        if (!window.confirm(`Delete signature "${s.name}"?`)) return;
        try {
            await axios.delete(`${API}/signatures/${s.signature_id}`, { headers: getAuthHeader() });
            toast.success('Signature deleted');
            await refresh();
        } catch (err) {
            toast.error(errorText(err, 'Failed to delete signature'));
        }
    };

    const handleCopy = async (s) => {
        setCopying(s.signature_id);
        try {
            await axios.post(`${API}/signatures/${s.signature_id}/fork`, {}, { headers: getAuthHeader() });
            toast.success(`Copied "${s.name}" to your signatures`);
            await refresh();
        } catch (err) {
            toast.error(errorText(err, 'Failed to copy signature'));
        } finally {
            setCopying(null);
        }
    };

    // Org masters can carry their author's user_id; they belong under Organisation only.
    const { mine, shared } = splitByOwner(signatures.filter(s => !s.is_admin_template), user?.user_id);
    const row = (isOwner) => (s) => (
        <ItemRow
            key={s.signature_id}
            item={s}
            idKey="signature_id"
            kind="signature"
            isOwner={isOwner}
            extraBadges={(
                <>
                    {isOwner && s.is_default && (
                        <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-amber-600">
                            <Star className="w-3 h-3" /> Default
                        </span>
                    )}
                    {overrideId && s.signature_id === overrideId && (
                        <span className="text-[10px] text-stone-400">· Set by your organisation</span>
                    )}
                </>
            )}
            onEdit={openEdit}
            onDelete={handleDelete}
            onCopy={handleCopy}
            copying={copying}
        />
    );

    const typedInDetails = form.visibility === 'shared' && form.content && !hasSenderFields(form.content);

    if (loading) return <p className="text-sm text-stone-400 py-6">Loading signatures…</p>;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <p className="text-sm text-stone-500">Signatures you can pick in every composer.</p>
                <Button onClick={openNew} className="bg-stone-900 hover:bg-stone-800" data-testid="signature-new-btn">
                    <Plus className="w-4 h-4 mr-1" /> New signature
                </Button>
            </div>
            <ItemGroup
                title="Mine" testId="signatures-mine" items={mine}
                emptyText="You have no signatures yet."
                renderRow={row(true)}
            />
            <ItemGroup
                title="Shared with me" testId="signatures-shared" items={shared}
                description="Their merge fields fill in from your own profile when you send."
                emptyText="Nobody has shared a signature with you yet."
                renderRow={row(false)}
            />
            <ItemGroup
                title="Organisation" testId="signatures-org" items={adminSignatures}
                description="Managed by your admins. Make a copy to change one."
                emptyText="Your organisation has no shared signatures."
                renderRow={row(false)}
            />

            <SignatureBuilderDialog
                open={editing !== null}
                onOpenChange={open => { if (!open) setEditing(null); }}
                title={editing?.signature_id ? 'Edit signature' : 'New signature'}
                description="Your own signature. Only you can use it unless you share it."
                mergeFields
                editorKey={editorKey}
                value={form.content}
                onChange={html => setForm(p => ({ ...p, content: html }))}
                signatureData={signatureData}
                onDataChange={setSignatureData}
                htmlOnly={htmlOnly}
                onHtmlChange={html => setForm(p => ({ ...p, content: html }))}
                resolvePreview={html => resolveSigVars(html, user, user?.email || '')}
                name={form.name}
                onNameChange={name => setForm(p => ({ ...p, name }))}
                visibility={(
                    <div className="space-y-4">
                        <VisibilityCards idPrefix="signature-visibility" value={form.visibility}
                            onChange={visibility => setForm(p => ({ ...p, visibility }))} />
                        <ShareTargets
                            control="switch"
                            visibility={form.visibility}
                            sharedWith={form.shared_with}
                            sharedWithGroups={form.shared_with_groups}
                            onChange={targets => setForm(p => ({ ...p, ...targets }))}
                            members={members}
                            groups={groups}
                            currentUserId={user?.user_id}
                            idPrefix="signature-share"
                        />
                    </div>
                )}
                generalExtra={typedInDetails && (
                    <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800"
                        data-testid="signature-typed-details-warning">
                        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                        <div className="flex-1">
                            People you share this with will send <strong>your</strong> name and details: this
                            signature has them typed in instead of merge fields.
                            {signatureData?.fields && (
                                <div className="mt-2">
                                    <Button type="button" size="sm" variant="outline" onClick={convertToMergeFields}
                                        data-testid="signature-use-merge-fields">
                                        Use merge fields
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                footerStart={(
                    <div className="flex items-center gap-2">
                        <Switch id="signature-default" checked={form.is_default}
                            onCheckedChange={is_default => setForm(p => ({ ...p, is_default }))}
                            data-testid="signature-default-switch" />
                        <Label htmlFor="signature-default">Use as my default signature</Label>
                    </div>
                )}
                onSave={handleSave}
                saving={saving}
                saveLabel={editing?.signature_id ? 'Update signature' : 'Create signature'}
            />
        </div>
    );
}
